import { useState, useEffect, useRef, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { usePromoteRoutinePrompt } from "../../hooks/use-routines";
import type { Routine } from "../../../lib/types";
import { modalOverlay, modalBox, fieldStyle, labelStyle, inputStyle } from "./task-modal-shared";

interface PromoteToPromptModalProps {
  open: boolean;
  onClose: () => void;
  routine: Routine;
  onPromoted?: (promptId: string) => void;
}

/**
 * Sprint 14 — the explicit "Promote to shared Prompt" gesture. Moves a Routine's
 * inline prompt_body into a reusable Prompt node (clearing prompt_body, setting
 * prompt_ref). Only meaningful when the routine still carries an inline body.
 */
export function PromoteToPromptModal({ open, onClose, routine, onPromoted }: PromoteToPromptModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const promote = usePromoteRoutinePrompt();

  useEffect(() => {
    if (open) {
      setName(routine.name);
      setDescription(routine.purpose);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open, routine.name, routine.purpose]);

  if (!open) return null;

  const alreadyShared = !routine.prompt_body;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || alreadyShared) return;
    promote.mutate(
      { routine_id: routine.id, prompt_name: name.trim(), prompt_description: description.trim() || undefined },
      {
        onSuccess: (res) => {
          showToast("Prompt promoted — the routine now references it");
          onPromoted?.(res.prompt_id);
          onClose();
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : "Something went wrong";
          showToast(`Couldn't promote: ${msg}`, "error", true);
        },
      },
    );
  }

  return (
    <div style={modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalBox} role="dialog" aria-label="Promote to shared prompt">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={iconBox}>
            <i className="ti ti-arrow-up-right" aria-hidden="true" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>Promote to shared Prompt</div>
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>Reuse this routine's prompt across agents</div>
          </div>
        </div>

        {alreadyShared ? (
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 16 }}>
            This routine already references a shared Prompt — there's no inline body to promote.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Prompt name</label>
              <input ref={nameRef} type="text" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Description</label>
              <input type="text" style={inputStyle} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
              <button type="button" onClick={onClose} style={{ fontSize: 13, padding: "8px 14px" }}>Cancel</button>
              <button type="submit" data-variant="primary" disabled={!name.trim() || promote.isPending} style={{ fontSize: 13, padding: "8px 14px" }}>
                {promote.isPending ? "Promoting..." : "Promote"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const iconBox: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--color-text-secondary)",
  fontSize: 20,
  flexShrink: 0,
};
